import { ISMEDesignGuidelinesExtended, DropdownOption } from './SMEDesignGuidelineTypes';
import { ISMEDesignGuidelineFilter } from './SMEDesignGuidelineFilter';
import { IUnitConfiguration } from '../../../Models/IUnitConfiguration';
import { ApiService } from '../../../Services/ApiService';
import logger from '../../../Services/LoggingService';

/**
 * Factory function to create API operations with proper closure scope
 */
export const createApiOperations = (
  apiService: ApiService,
  logPerformance: (label: string, startTime: number) => void
) => {
  /**
   * Get all design guidelines from the API
   */
  const getAllDesignGuidelines = async (
    isInitialApiCallComplete: boolean,
    currentData: ISMEDesignGuidelinesExtended[],
    setCurrentData: (data: ISMEDesignGuidelinesExtended[]) => void,
    setIsInitialApiCallComplete: (complete: boolean) => void,
    setFilteredData: (data: ISMEDesignGuidelinesExtended[]) => void,
    setIsLoading: (loading: boolean) => void,
    filterDesignGuidelines: (data: ISMEDesignGuidelinesExtended[]) => ISMEDesignGuidelinesExtended[]
  ) => {
    const startTime = performance.now();
    setIsLoading(true);
    try {
      let sessionData;
      
      if (!isInitialApiCallComplete) {
        logger.debug('Fetching design guidelines from API', null, {
          component: 'SMEDesignGuideline',
          method: 'getAllDesignGuidelines'
        });
        
        const fetchStart = performance.now();
        const response = await apiService.getAsync('settings/smedesignguidelines/latest/all');
        logPerformance('API fetch time', fetchStart);
        
        if (!response.ok) {
          const res = await response.text();
          throw Error(`Could not load SME design guidelines. ${response.status} ${res}`);
        }

        logger.debug('Parsing JSON response', null, {
          component: 'SMEDesignGuideline',
          method: 'getAllDesignGuidelines'
        });
        
        const jsonStart = performance.now();
        const rawData = await response.json();
        logPerformance('JSON parse time', jsonStart);
        
        logger.info('Guidelines received from API', { 
          count: rawData.length 
        }, {
          component: 'SMEDesignGuideline',
          method: 'getAllDesignGuidelines'
        });
          
        logger.debug('Setting state with received data', null, {
          component: 'SMEDesignGuideline',
          method: 'getAllDesignGuidelines'
        });
        
        const setStateStart = performance.now();
        setCurrentData(rawData);
        logPerformance('setCurrentData time', setStateStart);
        
        const setFlagStart = performance.now();
        setIsInitialApiCallComplete(true);
        logPerformance('setIsInitialApiCallComplete time', setFlagStart);
        
        logger.debug('Filtering data', null, {
          component: 'SMEDesignGuideline',
          method: 'getAllDesignGuidelines'
        });
        
        const filterStart = performance.now();
        const filtered = filterDesignGuidelines(rawData);
        logPerformance('Initial filtering time', filterStart);
        
        logger.debug('Setting filtered data state', null, {
          component: 'SMEDesignGuideline',
          method: 'getAllDesignGuidelines'
        });
        
        const setFilteredStart = performance.now();
        setFilteredData(filtered);
        logPerformance('setFilteredData time', setFilteredStart);
        
        const loadingEndStart = performance.now();
        setIsLoading(false);
        logPerformance('setIsLoading(false) time', loadingEndStart);
        
        logger.info('getAllDesignGuidelines completed successfully', {
          totalGuidelines: rawData.length,
          filteredGuidelines: filtered.length
        }, {
          component: 'SMEDesignGuideline',
          method: 'getAllDesignGuidelines'
        });
        
        return;
      } else {
        sessionData = currentData;
        const filtered = filterDesignGuidelines(sessionData);
        setFilteredData(filtered);
      }
    } catch (error) {
      logger.error('Failed to fetch design guidelines', error as Error, null, {
        component: 'SMEDesignGuideline',
        method: 'getAllDesignGuidelines'
      });
      setIsLoading(false);
    } finally {
      logPerformance('getAllDesignGuidelines total', startTime);
    }
  };

  /**
   * Get all unit configurations to populate dropdowns
   */
  const getAllUnitConfigs = async (
    setIndustries: (data: DropdownOption[]) => void,
    setSubIndustries: (data: DropdownOption[]) => void,
    setApplications: (data: DropdownOption[]) => void,
    setSubApplications: (data: DropdownOption[]) => void,
    setProductLines: (data: DropdownOption[]) => void,
    setProcessStages: (data: DropdownOption[]) => void,
    setAlfaLavalPositions: (data: DropdownOption[]) => void
  ) => {
    const startTime = performance.now();
    try {
      logger.debug('Fetching unit configurations', null, {
        component: 'SMEDesignGuideline',
        method: 'getAllUnitConfigs'
      });
      
      const response = await apiService.getAsync('settings/unitconfigurations');
      logger.debug('Unit configurations API response received', { 
        status: response.status,
        ok: response.ok 
      }, {
        component: 'SMEDesignGuideline',
        method: 'getAllUnitConfigs'
      });
      
      if (!response.ok) {
        throw Error(`Failed to fetch unit configurations: ${response.statusText}`);
      }
      
      logger.debug('Parsing unit configurations JSON', null, {
        component: 'SMEDesignGuideline',
        method: 'getAllUnitConfigs'
      });
      
      const parseStart = performance.now();
      const data = await response.json();
      const unitConfigData: IUnitConfiguration[] = data;
      logPerformance('Unit configurations JSON parse', parseStart);
      
      logger.info('Unit configurations received', { 
        count: unitConfigData.length 
      }, {
        component: 'SMEDesignGuideline',
        method: 'getAllUnitConfigs'
      });
      
      // Process unique values for dropdowns
      logger.debug('Populating dropdowns', null, {
        component: 'SMEDesignGuideline',
        method: 'getAllUnitConfigs'
      });
      
      const dropdownsStart = performance.now();
      populateDropdowns(
        unitConfigData,
        setIndustries,
        setSubIndustries,
        setApplications,
        setSubApplications,
        setProductLines,
        setProcessStages,
        setAlfaLavalPositions
      );
      logPerformance('Populate dropdowns', dropdownsStart);
    } catch (error) {
      logger.error('Failed to fetch unit configurations', error as Error, null, {
        component: 'SMEDesignGuideline',
        method: 'getAllUnitConfigs'
      });
    } finally {
      logPerformance('getAllUnitConfigs total', startTime);
    }
  };

  /**
   * Add or update a design guideline
   */
  const addNewDesignGuideline = async (
    selectedIndustry: string,
    selectedSubIndustry: string,
    selectedApplication: string,
    selectedSubApplication: string,
    selectedPosition: string,
    selectedProcessStage: string,
    selectedProductLine: string,
    designSuggestion: string,
    setIsLoading: (loading: boolean) => void,
    hideModal: () => void,
    setCurrentData: (data: ISMEDesignGuidelinesExtended[]) => void,
    setFilteredData: (data: ISMEDesignGuidelinesExtended[]) => void,
    filterDesignGuidelines: (data: ISMEDesignGuidelinesExtended[]) => ISMEDesignGuidelinesExtended[]
  ) => {
    try {
      setIsLoading(true);
      hideModal();
      
      const body = {
        industry: selectedIndustry,
        subIndustry: selectedSubIndustry,
        application: selectedApplication,
        subApplication: selectedSubApplication,
        alfaLavalPosition: selectedPosition,
        processStage: selectedProcessStage,
        productLine: selectedProductLine,
        designSuggestion: designSuggestion
      };
      
      const response = await apiService.postAsync(body, `settings/smedesignguidelines`);
      
      if (!response.ok) {
        const res = await response.text();
        throw Error(`Could not send response. ${response.status} ${res}`);
      }
      
      // Refresh data after adding new guideline
      logger.debug('Refreshing guidelines after adding new one', null, {
        component: 'SMEDesignGuideline',
        method: 'addNewDesignGuideline'
      });
      
      const refreshResponse = await apiService.getAsync('settings/smedesignguidelines/latest/all');
      logger.debug('Refresh API response received', { 
        status: refreshResponse.status,
        ok: refreshResponse.ok 
      }, {
        component: 'SMEDesignGuideline',
        method: 'addNewDesignGuideline'
      });
      
      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        setCurrentData(refreshData);
        
        // Apply current filters to new data
        const filtered = filterDesignGuidelines(refreshData);
        setFilteredData(filtered);
        
        logger.info('New design guideline added successfully', {
          totalGuidelines: refreshData.length,
          filteredGuidelines: filtered.length
        }, {
          component: 'SMEDesignGuideline',
          method: 'addNewDesignGuideline'
        });
      }
    } catch (error) {
      logger.error('Failed to add design guideline', error as Error, null, {
        component: 'SMEDesignGuideline',
        method: 'addNewDesignGuideline'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    getAllDesignGuidelines,
    getAllUnitConfigs,
    addNewDesignGuideline
  };
};

/**
 * Populate all dropdown options from unit configuration data
 */
const populateDropdowns = (
  unitConfigData: IUnitConfiguration[],
  setIndustries: (data: DropdownOption[]) => void,
  setSubIndustries: (data: DropdownOption[]) => void,
  setApplications: (data: DropdownOption[]) => void,
  setSubApplications: (data: DropdownOption[]) => void,
  setProductLines: (data: DropdownOption[]) => void,
  setProcessStages: (data: DropdownOption[]) => void,
  setAlfaLavalPositions: (data: DropdownOption[]) => void
) => {
  const startTime = performance.now();
  
  // Industries
  const industryStart = performance.now();
  const uniqueIndustries = Array.from(new Set(unitConfigData.map(item => item.industry)));
  setIndustries(uniqueIndustries.map(ind => ({ key: ind, text: ind })));
  
  // Sub Industries
  const subIndustryStart = performance.now();
  const uniqueSubIndustries = Array.from(new Set(unitConfigData.map(item => item.subIndustry)));
  setSubIndustries(uniqueSubIndustries.map(ind => ({ key: ind, text: ind })));
  
  // Applications
  const appStart = performance.now();
  const uniqueApplications = Array.from(new Set(unitConfigData.map(item => item.application)));
  setApplications(uniqueApplications.map(app => ({ key: app, text: app })));
  
  // Sub Applications
  const subAppStart = performance.now();
  const uniqueSubApplications = Array.from(new Set(unitConfigData.map(item => item.subApplication)));
  setSubApplications(uniqueSubApplications.map(subapp => ({ key: subapp, text: subapp })));
  
  // Product Lines
  const prodLineStart = performance.now();
  const uniqueProductLines = Array.from(new Set(unitConfigData.map(item => item.productLine)));
  setProductLines(uniqueProductLines.map(prod => ({ key: prod, text: prod })));
  
  // Process Stages
  const procStageStart = performance.now();
  const uniqueProcessStages = Array.from(new Set(unitConfigData.map(item => item.processStage)));
  setProcessStages(uniqueProcessStages.map(proc => ({ key: proc, text: proc })));
  
  // Alfa Laval Positions
  const positionsStart = performance.now();
  const uniquePositions = Array.from(new Set(unitConfigData.map(item => item.alfaLavalPosition)));
  setAlfaLavalPositions(uniquePositions.map(pos => ({ key: pos, text: pos })));
  
  logger.debug('Dropdowns populated successfully', {
    industries: uniqueIndustries.length,
    subIndustries: uniqueSubIndustries.length,
    applications: uniqueApplications.length,
    subApplications: uniqueSubApplications.length,
    productLines: uniqueProductLines.length,
    processStages: uniqueProcessStages.length,
    positions: uniquePositions.length,
    totalDuration: `${(performance.now() - startTime).toFixed(2)}ms`
  }, {
    component: 'SMEDesignGuideline',
    method: 'populateDropdowns'
  });
}; 